// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { selectNarrateGptEnabled } from "../../user-context/slice"
import { Code, executorMutation, isConnectError, timelineService } from "../../../app/rpc"
import { CreateTimeGroupRequestSchema, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type CreateTimeGroupRpc = (payload: Actions.CreateTimeGroupPayload) => Observable<TimeGroup>

export const createTimeGroup = (createTimeGroupRpc: CreateTimeGroupRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimeGroup.match),
      mergeMap(action => createTimeGroupRpc(action.payload)
        .pipe(
          withLatestFrom(state$),
          mergeMap(([timeGroupFromResponse, state]) => {
            const timeGroup = timeGroupFromResponse
            const timeRowIdsList = action.payload.request.timeRowIds ?? []
            const activeTimeRowIdsByDate = state.timeline.activeTimeRowIdsByDate[state.timeline.activeDate] ?? []
            const resetActiveTimeRowIdsByDate = timeRowIdsList.length !== 0
              && timeRowIdsList.every(id => activeTimeRowIdsByDate.includes(id))
            const actions: Action[] = [
              TimelineSlice.upsertTimeGroup(timeGroup),
              TimelineSlice.setTimeRowsGroupId({
                timeRowIds: timeRowIdsList,
                timeGroupId: timeGroup.id,
              }),
              Actions.generateTimesheetFieldConfigs([timeGroup]),
              TimelineSlice.addActiveTimeGroupId(timeGroup.id),
              TimelineSlice.removeTimeRowIdsInGroupingProgress(timeRowIdsList)
            ]
            if (resetActiveTimeRowIdsByDate) {
              actions.unshift(TimelineSlice.setActiveTimeRowIds({ timeRowIds: []}))
            }
            if (action.payload.post) {
              actions.push(Actions.postTimeGroup(timeGroup))
            } else if (selectNarrateGptEnabled(state) && TimelineAdditionalUiSlice.selectAutoNarrative(state)) {
              actions.push(
                TimelineAdditionalUiSlice.addAutoNarrativeTimeGroupId(timeGroup.id)
              )
            }
            return concat(actions)
          }),
          catchError((e: unknown) => concat(
            of(TimelineSlice.removeTimeRowIdsInGroupingProgress(action.payload.request.timeRowIds ?? [])),
            handleError(e),
          )),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_create_time_group_failed_precondition", message: "Failed to create timesheet. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_create_time_group_permission_denied", message: "Failed to create timesheet. You do not have permission to group the selected time rows." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_create_time_group_failed", message: "Failed to create the timesheet. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const createTimeGroupRpc = (payload: Actions.CreateTimeGroupPayload): Observable<TimeGroup> =>
  executorMutation(() => timelineService.createTimeGroup(create(CreateTimeGroupRequestSchema, payload.request)))

export default createTimeGroup(createTimeGroupRpc)
